/**
 * Readiness state for the MBMR editor.
 *
 * One recipe-level fetch when the editor opens; after a save, a single-operation
 * refresh (the contract's `?operation_id=` form) merged back into the tree.
 * No polling, no refetch on tab clicks — that is the whole performance budget.
 *
 * Consumers: ReadinessRail (right pane), ReadinessSubmitButton (editor header),
 * and the readiness dots.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useResponsive } from '@/hooks/useResponsive';
import { getReadiness } from '@/utils/mbmr_readiness_api';
import { normalizeReadiness, normalizeLifecycle, buildIndex, rollUpState } from './readinessModel';

const ReadinessContext = createContext(null);

export const useReadiness = () => useContext(ReadinessContext);

export function ReadinessProvider({ recipeId, onNavigateToOperation, children }) {
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState(null); // 'live' | 'mock'
  // The rail must not steal width on a narrow screen: below antd's `xl` (1200px) it
  // starts collapsed and gives its 300px back to the operation pane, exactly like the
  // left hierarchy drawer. A manual toggle wins from then on, at any width.
  const { isXl } = useResponsive();
  const [railOpen, setRailOpenState] = useState(isXl);
  const railUserSet = useRef(false);
  const [blockersFocused, setBlockersFocused] = useState(false);
  const abortRef = useRef(null);

  const setRailOpen = useCallback((next) => {
    railUserSet.current = true;
    setRailOpenState(next);
  }, []);

  useEffect(() => {
    if (!railUserSet.current) setRailOpenState(Boolean(isXl));
  }, [isXl]);

  const operationId = useSelector((s) => s.processConfig?.operation_id);
  const processId = useSelector((s) => s.processConfig?.process_id);

  // ── one recipe-level fetch per editor open ────────────────────────────────
  // Also re-run on demand after a lifecycle action (a return, a re-submit): those change the
  // approval state of every process at once, so a whole-recipe reload is the honest refresh.
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  useEffect(() => {
    if (!recipeId) return undefined;
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    getReadiness({ recipe_id: recipeId }, controller.signal)
      .then(({ data, source: src }) => {
        setModel(normalizeReadiness(data));
        setSource(src);
      })
      .catch((e) => {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setError(e?.message || 'Could not load readiness');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [recipeId, reloadKey]);

  /**
   * Splice a fresh lifecycle block in without refetching the tree. The return and re-submit
   * endpoints answer with the same block the readiness payload carries, so the header, the
   * rail and the tree icons update the moment the action returns — the full `reload()` then
   * follows to pick up anything else that moved.
   */
  const applyLifecycle = useCallback((raw) => {
    setModel((prev) => (prev ? { ...prev, lifecycle: normalizeLifecycle(raw) } : prev));
  }, []);

  /**
   * Refresh exactly one operation after a save and splice it into the tree,
   * re-deriving that process's roll-up and the recipe summary locally.
   */
  const refreshOperation = useCallback(
    async (opId) => {
      if (!opId || !recipeId) return;
      try {
        const { data, source: src } = await getReadiness({ operation_id: opId });
        const fresh = normalizeReadiness(data);
        const op = fresh.operations?.[0];
        if (!op) return;
        setSource(src);
        setModel((prev) => {
          if (!prev) return fresh;
          const processes = prev.processes.map((p) => {
            if (!p.operations.some((o) => String(o.operation_id) === String(opId))) return p;
            const operations = p.operations.map((o) =>
              String(o.operation_id) === String(opId) ? op : o
            );
            return { ...p, operations, state: rollUpState(operations.map((x) => x.state)) };
          });
          const all = processes.flatMap((p) => p.operations);
          const count = (s) => all.filter((o) => o.state === s).length;
          const blocked = count('blocked');
          return {
            ...prev,
            processes,
            operations: all,
            summary: {
              ...prev.summary,
              operations_total: all.length,
              ready: count('ready'),
              partial: count('partial'),
              blocked,
              not_started: count('not_started'),
              blocking_count: blocked,
              submittable: blocked === 0,
            },
          };
        });
      } catch (e) {
        // A failed refresh must never break the editor — the rail keeps the last
        // known state and the next recipe-level load corrects it.
        console.warn('readiness refresh failed', e);
      }
    },
    [recipeId]
  );

  const index = useMemo(() => buildIndex(model), [model]);

  const selectedOperation = useMemo(
    () => (operationId ? index.byOperation[operationId] || null : null),
    [index, operationId]
  );

  const blockers = useMemo(
    () => (model?.operations || []).filter((o) => o.state === 'blocked'),
    [model]
  );

  /**
   * The rail only knows an operation_id; the editor needs the process too (the operation
   * pane keys off process_id + operation_id together). Resolve it here from the index so
   * every caller can stay a one-argument click handler.
   */
  const navigateToOperation = useCallback(
    (opId) => {
      const op = index.byOperation[opId];
      if (!op || !onNavigateToOperation) return;
      onNavigateToOperation(op);
    },
    [index, onNavigateToOperation]
  );

  const openBlockers = useCallback(() => {
    railUserSet.current = true;
    setRailOpenState(true);
    setBlockersFocused(true);
    // Let the pane mount before the highlight clears itself.
    window.setTimeout(() => setBlockersFocused(false), 2200);
  }, []);

  const value = useMemo(
    () => ({
      model,
      index,
      loading,
      error,
      source,
      blockers,
      selectedOperation,
      selectedProcessState: processId ? index.byProcess[processId] : null,
      refreshOperation,
      onNavigateToOperation: navigateToOperation,
      railOpen,
      setRailOpen,
      blockersFocused,
      openBlockers,
      // RDY-LIFE: the record's approval life. `known: false` while the server has not sent a
      // lifecycle block — the header withholds BOTH the submit offer and the return action
      // rather than guessing that an unknown state is a draft.
      lifecycle: model?.lifecycle || normalizeLifecycle(null),
      applyLifecycle,
      reload,
    }),
    [
      setRailOpen,
      model,
      index,
      loading,
      error,
      source,
      blockers,
      selectedOperation,
      processId,
      refreshOperation,
      navigateToOperation,
      railOpen,
      blockersFocused,
      openBlockers,
      applyLifecycle,
      reload,
    ]
  );

  return <ReadinessContext.Provider value={value}>{children}</ReadinessContext.Provider>;
}

export default ReadinessContext;
